// Copyright (c) StrangeBee 2023

import { useCallback, useState } from 'react';
import { FilterInput } from 'thehive-sdk';

export type FilterInputForm = Partial<FilterInput>;

export interface ManageFiltersOutput {
    filters: FilterInputForm[];
    tempFilters: FilterInputForm[];
    editFilters: boolean;
    setFilters: (callback: (filters: FilterInputForm[]) => FilterInputForm[]) => void;
    setTempFilters: (callback: (tempFilters: FilterInputForm[]) => FilterInputForm[]) => void;
    setEditFilters: (editFilters: boolean) => void;
    applyFilters: () => void;
    removeFilter: (index: number) => void;
    removeTempFilter: (index: number) => void;
    addTempFilter: (filter: FilterInputForm) => void;
    clearFilters: () => void;
}

interface UseFiltersReturn {
    editFilters: boolean;
    setEditFilters: React.Dispatch<React.SetStateAction<boolean>>;
    filters: FilterInputForm[];
    setFilters: React.Dispatch<React.SetStateAction<FilterInputForm[]>>;
    addFilter: (filter: FilterInput) => void;
    removeFilter: (index: number) => void;
    tempFilters: FilterInputForm[];
    setTempFilters: React.Dispatch<React.SetStateAction<FilterInputForm[]>>;
    addTempFilter: (filter: FilterInputForm) => void;
    removeTempFilter: (index: number) => void;
    applyFilters: () => void;
    clearFilters: () => void;
}

const useFilters = (initialValue: FilterInput[] = []): UseFiltersReturn => {
    const [editFilters, setEditFilters] = useState(false);

    const [filters, setFilters] = useState<FilterInputForm[]>(initialValue);

    const [tempFilters, setTempFilters] = useState<FilterInputForm[]>(initialValue);

    const addFilter = (filter: FilterInput): void => {
        setFilters([...filters, filter]);
    };

    const addTempFilter = useCallback(
        (filter: FilterInputForm): void => {
            setTempFilters([...tempFilters, filter]);
        },
        [tempFilters],
    );

    const removeTempFilter = (index: number): void => {
        const tempFiltersProcessed = [...tempFilters];
        tempFiltersProcessed.splice(index, 1);
        setTempFilters(tempFiltersProcessed);
    };

    const applyFilters = (): void => {
        setFilters(tempFilters);
    };

    const clearFilters = useCallback((): void => {
        setTempFilters([]);
        setFilters([]);
    }, [setTempFilters, setFilters]);

    const removeFilter = useCallback(
        (index: number): void => {
            const filtersProcessed = [...filters];
            filtersProcessed.splice(index, 1);
            setFilters(filtersProcessed);
        },
        [filters, setFilters],
    );

    return {
        editFilters,
        setEditFilters,
        filters,
        setFilters,
        addFilter,
        removeFilter,
        tempFilters,
        setTempFilters,
        addTempFilter,
        removeTempFilter,
        applyFilters,
        clearFilters,
    };
};

export default useFilters;
